<template>
  <div class="flex-grow flex flex-col w-full relative">
    <ChatMessagesArea
      :user-id="$page?.props?.user?.id"
      :clinicianName="clinicianName"
      :messages-data="filteredMessages"
    ></ChatMessagesArea>

    <!-- Actions -->

    <div class="flex flex-col w-full mx-auto p-1 relative bg-transparent">
      <div class="z-10 flex flex-col relative" :class="{ 'space-y-8': hasClinicianMention }">
        <!-- Microcopy hint for @clinician mention -->
        <ClinicianMentionHint
          v-if="!$page.props.isClinician"
          @mention-click="insertClinicianMention"
        />

        <!-- File attachment Preview -->
        <div v-show="showAttachmentPreview" class="relative mb-4">
          <div class="relative rounded w-24 h-24" v-show="isImage">
            <XCircleIcon
              v-if="!sendingMessage"
              class="absolute left-1 top-1 h-6 w-6 text-white cursor-pointer"
              @click="clearAttachment"
            />
            <img
              :alt="attachmentFile?.name"
              ref="attachmentPreview"
              class="rounded w-full h-full object-cover"
            />

            <span
              v-if="sendingMessage"
              class="
                absolute
                left-0
                top-0
                items-center
                justify-center
                flex flex-col
                w-full
                h-full
                bg-dark-grey bg-opacity-70
                select-none
                rounded
              "
            >
              <div class="w-5 h-5 border-white border-2 rounded-full border-t-0 animate-spin"></div>
              <span class="text-white text-xxs">Uploading...</span>
            </span>
          </div>

          <div
            v-show="!isImage && !errorMessage"
            class="flex items-center gap-1 text-vice-teal-700"
          >
            <DocumentIcon class="h-10 w-10 min-w-[2.5rem]" />
            {{ attachmentFile?.name }}
            <XCircleIcon
              v-if="!sendingMessage"
              class="h-6 w-6 min-w-[1.2rem] text-white cursor-pointer"
              @click="clearAttachment"
            />

            <span v-if="sendingMessage" class="h-6 w-6 min-w-[1.2rem] text-white cursor-pointer">
              <div
                class="mr-2 w-5 h-5 border-white border-2 rounded-full border-t-0 animate-spin"
              ></div>
            </span>
          </div>
        </div>
        <!-- End of file attachment Preview -->
        <div class="relative flex flex-col">
          <Transition>
            <div
              v-if="errorMessage"
              class="
                absolute
                bottom-full
                mb-4
                rounded
                p-2
                z-20
                bg-vice-light-red
                text-white text-xs
                flex
                items-center
              "
            >
              {{ errorMessage }}
              <XCircleIcon
                class="ml-2 h-6 w-6 text-white cursor-pointer"
                @click="errorMessage = ''"
              />
            </div>
          </Transition>

          <!-- Badge showing @clinician detected -->
          <Transition>
            <div
              v-if="hasClinicianMention"
              class="
                absolute
                -top-8
                left-0
                bg-vice-rose-50
                border border-vice-rose-400
                rounded-md
                px-3
                py-1
                text-xs text-vice-rose-600
                font-semibold
                flex
                items-center
                gap-2
              "
            >
              <OfficeBuildingIcon class="w-4 h-4" />
              <span>Sending to clinician</span>
            </div>
          </Transition>

          <textarea
            class="
              focus:border-vice-teal-500
              focus:outline-none
              focus:ring-0
              focus:shadow-none
              border
              rounded-md rounded-b-xl
              px-10
              py-3
              text-gray-900
              resize-none
            "
            :class="
              hasClinicianMention
                ? 'mt-0.5 border-vice-rose-400 focus:border-vice-rose-500'
                : 'border-gray-700'
            "
            placeholder="Type new message..."
            name="message"
            ref="messageInput"
            v-model="newMessage"
            @keypress.enter.prevent="addMessage"
            rows="1"
          ></textarea>

          <div class="text-white" :class="{ 'pointer-events-none': sendingMessage }">
            <div class="absolute left-2 bottom-3">
              <button class="relative mr-2 hidden">
                <EmojiHappyIcon class="w-6 h-6" />
              </button>

              <label class="cursor-pointer" for="attachmentInput">
                <input
                  type="file"
                  id="attachmentInput"
                  class="hidden"
                  @change="chooseFile"
                  accept=".jpeg,.bmp,.png,.pdf,.gif"
                />

                <Paperclip class="w-6 h-6 hover:text-vice-teal-700 text-grey" />
              </label>
            </div>
            <div class="absolute right-2 bottom-2">
              <button data-cy="send-message" v-if="!sendingMessage" @click="addMessage">
                <SendMessage class="w-6 h-6 hover:text-vice-teal-700 text-grey" />
              </button>

              <span v-if="sendingMessage" class="flex mr-1 items-center">
                <svg
                  class="animate-spin -ml-1 h-6 w-6"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="#14B8A6"
                    stroke-width="4"
                  />
                  <path
                    class=""
                    fill="#14B8A6"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { v4 as uuidv4 } from 'uuid';
import { EmojiHappyIcon, OfficeBuildingIcon, XCircleIcon } from '@heroicons/vue/solid';
import { DocumentIcon } from '@heroicons/vue/outline';
import duration from 'dayjs/plugin/duration';
import linkifyHtml from 'linkify-html';
import dayjs from 'dayjs';
import useSessionStore from '@/Stores/useSessionStore';
import useChatMessagesStore from '@/Stores/useChatMessagesStore';
import ChatMessagesArea from '@/Components/Chat/ChatMessagesArea.vue';
import ChatService from '@/Services/ChatService';
import {
  USER_ROLE,
  MAX_FILE_SIZE,
  VICE_MESSAGE_TYPE,
  VICE_SESSION_PHASES,
} from '@/Utils/vice-constants';
import handleAsyncRequest from '@/Utils/handleAsyncRequest';
import Paperclip from '@/Components/Icons/Paperclip.vue';
import SendMessage from '@/Components/Icons/SendMessage.vue';
import ClinicianMentionHint from '@/Components/Chat/ClinicianMentionHint.vue';

dayjs.extend(duration);

const chatMessagesStore = useChatMessagesStore();
const sessionStore = useSessionStore();

export default {
  props: {
    participantsList: {
      type: Object,
    },
    clinicianName: {
      type: String,
    },
    userIsObserver: {
      type: Boolean,
    },
  },
  components: {
    ChatMessagesArea,
    ClinicianMentionHint,
    XCircleIcon,
    DocumentIcon,
    EmojiHappyIcon,
    OfficeBuildingIcon,
    Paperclip,
    SendMessage,
  },

  data() {
    return {
      sendingMessage: false,
      errorMessage: '',
      userIsScrolling: false,
      showNewMessagesIndicator: false,
      newMessage: '',
      messageType: 'message',
      showAttachmentPreview: false,
      attachmentFile: null,
      stickyMessagesOpen: false,
      CONSULTATION: VICE_SESSION_PHASES.CONSULTATION,
      VICE_SESSION_PHASES,
      USER_ROLE,
      sessionStore,
    };
  },

  computed: {
    rawMessagesData() {
      return chatMessagesStore.allChatMessages;
    },
    messagesData() {
      return _.uniqBy(this.rawMessagesData, (e) => e.id);
    },
    isImage() {
      return this.attachmentFile?.type?.startsWith('image/');
    },
    messagesToClinician() {
      // Display the message the current user sent to the Clinician
      // If the clinician views your message, we add it to the chat list on the clinician tab
      return this.messagesData.filter(
        (msg) =>
          msg.sendTo === USER_ROLE.CLINICIAN &&
          !msg.handledAt &&
          dayjs().diff(dayjs(msg.sentAt), 's') < 60 &&
          msg.type !== 'attachment',
      );
    },
    messagesToClinicianQueue() {
      return this.messagesToClinician.filter((msg, index) => index != 0);
    },
    filteredMessages() {
      const currentUserId = sessionStore?.authUser?.id;
      // Clinicians and participants/observers are stored in separate tables with their own
      // auto-incrementing ids, so a Clinician.id and a User.id can collide (e.g. both being 1).
      // "Is this my own message" must therefore also check the sender's role, not just id.
      const currentUserType = sessionStore?.userIsObserver
        ? USER_ROLE.OBSERVER
        : USER_ROLE.PARTICIPANT;

      // During consultation
      if (sessionStore?.currentPhase === this.CONSULTATION) {
        // Clinician sees only messages directed to them
        if (sessionStore?.isClinician) {
          return this.messagesData.filter(
            (msg) => msg.sendTo === USER_ROLE.CLINICIAN || msg.sender.type === USER_ROLE.CLINICIAN,
          );
        }

        // Participant:
        // - hide clinician-only messages
        // - but still show participant's own messages
        // - always show attachments
        return this.messagesData.filter(
          (msg) =>
            msg.sendTo !== USER_ROLE.CLINICIAN ||
            msg.type === 'attachment' ||
            (msg.sender.id === currentUserId && msg.sender.type === currentUserType),
        );
      }

      if (sessionStore?.isClinician && sessionStore?.isPhasePaused) {
        return this.messagesData.filter(
          (msg) => msg.sendTo === USER_ROLE.CLINICIAN || msg.sender.type === USER_ROLE.CLINICIAN,
        );
      }

      return this.messagesData;
    },

    clinicianQuestions() {
      if (sessionStore?.isClinician && sessionStore?.currentPhase === VICE_SESSION_PHASES.DEBRIEF) {
        return this.messagesData.filter(
          (msg) =>
            msg.sendTo === USER_ROLE.CLINICIAN &&
            msg.phase.id !== VICE_SESSION_PHASES.DEBRIEF &&
            msg.content,
        );
      }
      return this.messagesData;
    },
    showSeparateChatsNotification() {
      return false;
    },
    formatedTime() {
      return this.messagesToClinician.map((msg) => dayjs(msg.sentAt).format('hh:mm'));
    },
    // Detect @clinician mention in message
    hasClinicianMention() {
      if (!this.newMessage) return false;
      const mentionRegex = /@clinician/i;
      return mentionRegex.test(this.newMessage);
    },
    computedSendTo() {
      return this.hasClinicianMention ? USER_ROLE.CLINICIAN : USER_ROLE.PARTICIPANT;
    },
  },

  methods: {
    messageContent(messageData) {
      const options = {
        defaultProtocol: 'https',
        className: 'underline break-all',
        target: '_blank',
      };
      return linkifyHtml(messageData.content, options);
    },
    async addMessage() {
      if (this.messageType === VICE_MESSAGE_TYPE.MESSAGE) {
        if (!this.newMessage.trim()) return;
        this.sendingMessage = true;
        chatMessagesStore
          .sendChatMessage({
            id: uuidv4(),
            content: this.newMessage,
            type: this.messageType,
            sendTo: this.computedSendTo,
          })
          .then(() => {
            this.sendingMessage = false;
          })
          .catch((error) => {
            console.error('Sending message error: ', error);
          });
        this.newMessage = '';
      }

      if (this.messageType === VICE_MESSAGE_TYPE.ATTACHMENT) {
        await this.uploadAttachment();
      }
    },

    chooseFile(event) {
      const attachment = event.target.files[0];
      const fileSize = attachment.size / 1000;
      if (fileSize > MAX_FILE_SIZE) {
        this.errorMessage =
          'The file is too large to upload. The maximum supported file size is 25MB.';
      } else {
        this.$refs.attachmentPreview.src = URL.createObjectURL(attachment);
        this.$refs.attachmentPreview.height = 80;
        this.attachmentFile = attachment;
        this.showAttachmentPreview = true;
        this.messageType = VICE_MESSAGE_TYPE.ATTACHMENT;
        // eslint-disable-next-line func-names
        this.$refs.attachmentPreview.onload = function () {
          URL.revokeObjectURL(this.src);
        };
      }
    },

    async uploadAttachment() {
      const formData = new FormData();
      formData.append('attachment', this.attachmentFile);
      formData.append('uuid', sessionStore?.sessionUniqueId);
      this.sendingMessage = true;
      const [res, err] = await handleAsyncRequest(ChatService.uploadAttachment(formData));

      if (!err) {
        this.sendingMessage = false;
        chatMessagesStore.sendChatMessage({
          id: uuidv4(),
          content: this.newMessage,
          type: this.messageType,
          sendTo: this.computedSendTo,
          attachment: JSON.stringify({
            uuid: sessionStore?.sessionUniqueId,
            fileName: res.filename,
            url: res.url,
            thumbnail: res.thumbnail_url ? res.thumbnail_url : '',
            fileType: res.mime_type,
          }),
        });
      } else {
        console.error(err);
      }
      this.sendingMessage = false;
      this.clearAttachment();
    },

    clearAttachment() {
      URL.revokeObjectURL(this.$refs.attachmentPreview.src);
      this.showAttachmentPreview = false;
      this.attachmentFile = null;
      this.messageType = VICE_MESSAGE_TYPE.MESSAGE;
      this.newMessage = '';
    },

    insertClinicianMention() {
      if (!this.hasClinicianMention) {
        this.newMessage = `@clinician ${this.newMessage}`;
      }
      this.$nextTick(() => {
        const input = this.$refs.messageInput;
        input.focus();
        input.setSelectionRange(input.value.length, input.value.length);
      });
    },
  },
  watch: {
    messagesToClinicianQueue(oldVal) {
      if (!oldVal.length) this.stickyMessagesOpen = false;
    },
  },
};
</script>

<style>
.v-enter-active,
.v-leave-active {
  transition: opacity 0.5s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}
</style>
